// src/lib/contentActivityRail.js
// Filepath: // src/lib/contentActivityRail.js
import { deriveAssetsFromArtUrl } from "./contentNormalizer";

/**
 * Normalize ONLY the items inside the `live` array and ONLY if
 * `sale.saleStatus === "LIVE"`.
 *
 * Output aligns with normalizeFreeArtwork/normalizeHolding so
 * GridScene / Item / Gallery can treat these uniformly.
 */

function s(v) {
  return typeof v === "string" ? v : "";
}

function pickArtist(art) {
  const full =
    s(art?.normalizedArtistName) ||
    [s(art?.artistFirstName), s(art?.artistLastName)].filter(Boolean).join(" ").trim();
  return full || null;
}

function pickBaseArtUrl(art) {
  // Prefer explicit artUrl if present, then CDN, then swayer blobUrl, then original.
  return (
    s(art?.artUrl) ||
    s(art?.location?.cdnUrl) ||
    s(art?.swayerResponse?.blobUrl) ||
    s(art?.location?.originalUrl) ||
    ""
  );
}

function expectedType(art) {
  return art?.expectedMediaType === "Video" ? "Video" : "Image";
}

function randomIdFallback() {
  try {
    const u8 = new Uint8Array(8);
    crypto.getRandomValues(u8);
    return Array.from(u8, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `id_${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Normalize ONLY the `live` array entries where saleStatus === "LIVE".
 * Derive proper assets via deriveAssetsFromArtUrl(baseArtUrl, expectedMediaType).
 */
export function normalizeActivityItems(apiResponse) {
  const live = Array.isArray(apiResponse?.live) ? apiResponse.live : [];

  return live
    .filter((entry) => s(entry?.sale?.saleStatus) === "LIVE")
    .map((entry) => {
      const art = entry?.art || {};
      const sale = entry?.sale || {};

      const id =
        s(art?.id) || s(art?.artworkId) || s(sale?.artworkId) || randomIdFallback();

      const title = s(art?.title) || "Untitled";
      const artist = pickArtist(art);
      const year = s(art?.year) || null;

      const type = expectedType(art); // "Video" | "Image"
      const base = pickBaseArtUrl(art);
      const assets = deriveAssetsFromArtUrl(base, type);
      const isVideo = type === "Video";

      const byline = [artist, year].filter(Boolean).join(" • ") || null;
      const description = s(art?.description) || null;

      // Build the normalized item matching other normalizers
      const normalized = {
        id,
        kind: "activity",
        title,
        subtitle: byline,                  // keep short on cards
        description,                       // richer detail for item page
        expectedMediaType: type,           // "Video" | "Image"
        isVideo,
        mediaUrl: isVideo ? assets.videoUrl : null,
        previewUrl: isVideo ? assets.previewUrl : null,
        imageUrl: !isVideo ? assets.imageUrl : null,
        backgroundUrl: assets.backgroundUrl,
        thumbnailUrl: assets.thumbnailUrl,

        // Optional extras consumers might use
        artistName: artist,
        year,
        isKbeanSelect: !!art?.isKbeanSelect,
        tags: s(art?.tags),

        // Sale context (may be displayed on detail)
        saleStatus: s(sale?.saleStatus), // "LIVE"
        isLive: true,
        pricePerShare: sale?.shares?.pricePerShare ?? null,
        originalPrice: art?.originalPrice ?? null,
        categories: Array.isArray(art?.categories) ? art.categories : [],

        __gridSource: "activityLive",
      };

      return normalized;
    })
    // Only keep items with a usable visual (thumb). For Video, we already
    // provide a thumbnail; for Image, we provide both imageUrl & thumb.
    .filter((x) => !!x.thumbnailUrl);
}

/**
 * Append the "Live Sales" rail to an existing rails array.
 * If no live items qualify, returns the input rails unchanged.
 */
export function addActivityRail(existingRails = [], apiResponse) {
  const items = normalizeActivityItems(apiResponse);
  if (!items.length) return existingRails;

  const rail = {
    id: "liveSales",
    title: "Live Sales",
    items,
    gridParams: { source: "activity", title: "Live Sales" },
  };

  return [...existingRails, rail];
}
